import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Select,
  FormControl,
  InputLabel,
  Stack,
  useMediaQuery,
  useTheme,
  Fade,
  Grow,
  alpha,
  Card,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  ContentCopy,
  Download,
  QrCode,
  Event,
  LocationOn,
  Note,
  Link as LinkIcon,
  CheckCircle,
  People,
  Today,
  AccessTime,
  Close,
} from "@mui/icons-material";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import dayjs from "dayjs";
import { QRCodeCanvas } from "qrcode.react";

export default function CreateMeeting() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [formData, setFormData] = useState({
    name: "",
    day: "",
    date: dayjs().format("YYYY-MM-DD"),
    startTime: "09:00",
    endTime: "10:00",
    location: "",
    notes: "",
  });
  const [link, setLink] = useState("");
  const [docId, setDocId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();

  const days = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const docRef = await addDoc(collection(db, "meetings"), {
        ...formData,
        createdAt: serverTimestamp(),
      });
      const meetingLink = `${window.location.origin}/register/${docRef.id}`;
      setLink(meetingLink);
      setDocId(docRef.id);
      setSnackbarMessage("Daftar hadir berhasil dibuat!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (err) {
      setError("Gagal membuat daftar hadir. Silakan coba lagi.");
      setSnackbarMessage("Gagal membuat daftar hadir");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(link);
    setSnackbarMessage("Link berhasil disalin!");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById("qr-code");
    if (!canvas) return;

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `qr_meeting_${docId}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    setSnackbarMessage("QR Code berhasil diunduh!");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
  };

  const resetForm = () => {
    setLink("");
    setDocId("");
    setFormData({
      name: "",
      day: "",
      date: dayjs().format("YYYY-MM-DD"),
      startTime: "09:00",
      endTime: "10:00",
      location: "",
      notes: "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
            transition: theme.transitions.create(
              ["box-shadow", "border-color"],
              {
                duration: theme.transitions.duration.standard,
              },
            ),
            "&:hover": {
              boxShadow: `0 6px 32px ${alpha(theme.palette.common.black, 0.08)}`,
            },
          })}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                fontWeight: 800,
                background: `linear-gradient(135deg, #1b5e20 0%, #2e7d32 40%, #4caf50 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                letterSpacing: -0.5,
              }}
            >
              Buat Daftar Hadir
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, fontWeight: 500 }}
            >
              Isi form di bawah untuk membuat agenda rapat baru
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                "& .MuiAlert-icon": { fontSize: 24 },
              }}
              action={
                <IconButton
                  size="small"
                  onClick={() => setError("")}
                  sx={{ color: "error.main" }}
                >
                  <Close fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Meeting Title */}
              <TextField
                fullWidth
                label="Judul Agenda Rapat"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                variant="outlined"
                placeholder="Masukkan judul rapat"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Event sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 3 },
                  },
                }}
              />

              {/* Date and Day */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Hari</InputLabel>
                    <Select
                      name="day"
                      value={formData.day}
                      onChange={handleChange}
                      required
                      label="Hari"
                      sx={{ borderRadius: 3 }}
                    >
                      {days.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Tanggal"
                    type="date"
                    name="date"
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Today sx={{ color: "text.secondary" }} />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 3 },
                      },
                    }}
                    value={formData.date}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              {/* Time */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Waktu Mulai"
                    type="time"
                    name="startTime"
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTime sx={{ color: "text.secondary" }} />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 3 },
                      },
                    }}
                    value={formData.startTime}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Waktu Akhir"
                    type="time"
                    name="endTime"
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTime sx={{ color: "text.secondary" }} />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 3 },
                      },
                    }}
                    value={formData.endTime}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              {/* Location */}
              <TextField
                fullWidth
                label="Lokasi"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                variant="outlined"
                placeholder="Masukkan lokasi rapat"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 3 },
                  },
                }}
              />

              {/* Notes */}
              <TextField
                fullWidth
                label="Catatan (Opsional)"
                name="notes"
                multiline
                rows={isMobile ? 3 : 4}
                value={formData.notes}
                onChange={handleChange}
                variant="outlined"
                placeholder="Tambahkan catatan atau informasi tambahan..."
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Note sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 3 },
                  },
                }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={(theme) => ({
                  py: 2,
                  borderRadius: 3,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  bgcolor: "#2e7d32",
                  boxShadow: `0 4px 16px ${alpha("#2e7d32", 0.3)}`,
                  "&:hover": {
                    bgcolor: "#1b5e20",
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 24px ${alpha("#2e7d32", 0.4)}`,
                  },
                  "&:disabled": {
                    bgcolor: alpha("#2e7d32", 0.6),
                  },
                  transition: theme.transitions.create(
                    ["background-color", "transform", "box-shadow"],
                    { duration: theme.transitions.duration.standard },
                  ),
                  textTransform: "none",
                })}
              >
                {loading ? (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <CircularProgress size={24} sx={{ color: "white" }} />
                    <Typography color="white">Membuat...</Typography>
                  </Stack>
                ) : (
                  "Buat Daftar Hadir"
                )}
              </Button>
            </Stack>
          </form>

          {/* Success Section */}
          {link && (
            <Grow in timeout={500}>
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ my: 3 }}>
                  <Chip
                    icon={<CheckCircle />}
                    label="Berhasil Dibuat!"
                    color="success"
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      "& .MuiChip-icon": { fontSize: 20 },
                    }}
                  />
                </Divider>

                <Card
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 4,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    bgcolor: alpha(theme.palette.success.main, 0.04),
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    align="center"
                    sx={{ fontWeight: 700, color: "#2e7d32" }}
                  >
                    Daftar Hadir Berhasil Dibuat!
                  </Typography>
                  <Typography
                    variant="body2"
                    paragraph
                    align="center"
                    color="text.secondary"
                  >
                    Bagikan link atau QR Code ini ke peserta:
                  </Typography>

                  {/* Link Section */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: 3,
                      bgcolor: "background.paper",
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Stack
                      direction={isMobile ? "column" : "row"}
                      spacing={1}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                          borderRadius: 2,
                          width: "100%",
                        }}
                      >
                        <LinkIcon
                          sx={{ color: "text.secondary", fontSize: 20 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: "break-all",
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                            flex: 1,
                          }}
                        >
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#2e7d32",
                              textDecoration: "none",
                              fontWeight: 500,
                            }}
                          >
                            {link}
                          </a>
                        </Typography>
                      </Box>
                      <Tooltip title="Salin Link" arrow>
                        <IconButton
                          onClick={copyToClipboard}
                          sx={{
                            color: "#2e7d32",
                            bgcolor: alpha("#2e7d32", 0.08),
                            "&:hover": {
                              bgcolor: alpha("#2e7d32", 0.15),
                            },
                            borderRadius: 2,
                          }}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>

                  {/* QR Code Section */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ fontWeight: 500 }}
                    >
                      <QrCode
                        sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                      />
                      QR Code untuk Peserta
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: "white",
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        display: "inline-block",
                        transition: theme.transitions.create("border-color", {
                          duration: theme.transitions.duration.short,
                        }),
                        "&:hover": {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                      }}
                    >
                      <QRCodeCanvas
                        id="qr-code"
                        value={link}
                        size={isMobile ? 150 : 200}
                        bgColor="#ffffff"
                        fgColor="#1b5e20"
                        level="H"
                      />
                    </Paper>
                    <Button
                      variant="outlined"
                      onClick={downloadQRCode}
                      startIcon={<Download />}
                      sx={{
                        mt: 2,
                        borderRadius: 3,
                        borderColor: "#2e7d32",
                        color: "#2e7d32",
                        "&:hover": {
                          borderColor: "#1b5e20",
                          bgcolor: alpha("#2e7d32", 0.05),
                        },
                      }}
                    >
                      Download QR Code
                    </Button>
                  </Box>

                  {/* Action Buttons */}
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={2}
                    sx={{ mt: 3 }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/report/${docId}`)}
                      fullWidth
                      startIcon={<People />}
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        bgcolor: "#2e7d32",
                        "&:hover": { bgcolor: "#1b5e20" },
                      }}
                    >
                      Lihat Rekap Kehadiran
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={resetForm}
                      fullWidth
                      startIcon={<Add />}
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        borderColor: "#2e7d32",
                        color: "#2e7d32",
                        "&:hover": {
                          borderColor: "#1b5e20",
                          bgcolor: alpha("#2e7d32", 0.05),
                        },
                      }}
                    >
                      Buat Rapat Lainnya
                    </Button>
                  </Stack>
                </Card>
              </Box>
            </Grow>
          )}
        </Paper>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          TransitionComponent={Grow}
        >
          <Alert
            severity={snackbarSeverity}
            sx={{
              borderRadius: 3,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.12)}`,
            }}
            action={
              <IconButton
                size="small"
                onClick={() => setOpenSnackbar(false)}
                sx={{ color: "inherit" }}
              >
                <Close fontSize="small" />
              </IconButton>
            }
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
}
